/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable, observable, runInAction, toJS } from "mobx"

import type { ApiErrorResponse } from "../network/http-client"

interface AppError {
    name: string
    message: string
}

export interface ErrorLog {
    timestamp?: number
    message: string
    apiError?: ApiErrorResponse
    appError?: AppError
}

export interface OkLog {
    timestamp?: number
    message: string
}

export class LoggerStore {
    private STORAGE_KEY = "IAP_logger_store"

    private showErrorTimeout = 4_000
    private showOkTimeout = 3_000

    private lastErrorTimeoutId: ReturnType<typeof setTimeout> | null = null
    private lastOkTimeoutId: ReturnType<typeof setTimeout> | null = null

    errors: ErrorLog[] = observable.array([])
    lastError: ErrorLog | null = null
    lastOkLog: OkLog | null = null

    constructor() {
        makeAutoObservable(this, {
            logError: true,
            logOk: true,
            logApiError: true,
            clearLastError: true,
            clearLastOkLog: true,
            clearErrors: true
        })
        this.errors.push(...this.loadErrors())
    }

    logApiError(message: string, apiError?: ApiErrorResponse, error?: Error): void {
        this.logError({
            message: message,
            apiError: apiError,
            appError: {
                name: error?.name ?? "",
                message: error?.message ?? ""
            }
        })
    }

    logError(err: ErrorLog): void {
        const withTimestamp = {
            timestamp: Date.now(),
            ...err
        }
        runInAction(() => {
            this.errors.unshift(withTimestamp) // put the item at the start for chrono render
            this.lastError = withTimestamp
            this.saveErrors(this.errors)
            this.startErrorDismissTimer()
        })
    }

    logErrorMessage(message: string): void {
        this.logError({
            message,
            appError: new Error(message)
        })
    }

    logOk(message: string): void {
        this.lastOkLog = { message }
        this.startOkDismissTimer()
    }

    // clear
    clearLastError(): void {
        this.lastError = null
    }

    clearLastOkLog(): void {
        this.lastOkLog = null
    }

    clearErrors(): void {
        // keep the array reactive, do not redefine or assign an empty array []
        this.errors.splice(0, this.errors.length)
        this.saveErrors(this.errors)
    }

    // private
    private startErrorDismissTimer(): void {
        if (this.lastErrorTimeoutId) {
            clearTimeout(this.lastErrorTimeoutId)
        }

        this.lastErrorTimeoutId = setTimeout(() => {
            runInAction(() => {
                this.lastError = null
                this.lastErrorTimeoutId = null
            })
        }, this.showErrorTimeout)
    }

    private startOkDismissTimer(): void {
        if (this.lastOkTimeoutId) {
            clearTimeout(this.lastOkTimeoutId)
        }
        this.lastOkTimeoutId = setTimeout(() => {
            runInAction(() => {
                this.lastOkLog = null
            })
        }, this.showOkTimeout)
    }

    private loadErrors(): ErrorLog[] {
        try {
            const storedErr = localStorage.getItem(this.STORAGE_KEY)
            return storedErr ? JSON.parse(storedErr) : []
        } catch {
            return []
        }
    }

    private saveErrors(errors: ErrorLog[]): void {
        try {
            // because of MobX we need to convert to simple JS, non-observable data
            const plainData = toJS(errors)
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(plainData))
        } catch {
            //this.logError(new Error("Failed to store errors to localStorage"));
            console.error("Could not save Errors to Local Storage")
        }
    }
}
